// apps/dashboard/src/lib/device.ts
//
// Storage access THROWS rather than returning null when the browser blocks it:
// mobile Chrome with third-party data off, iOS in-app webviews, Safari private
// mode. `getDeviceId()` builds the `X-Device-Id` header on EVERY api request
// (lib/api/client.ts) and runs during device registration in AuthContext, so an
// unguarded throw here failed every call at header-build time — a total outage
// for those users, not one degraded feature. Observed on prod as
// "SecurityError: Failed to read the 'sessionStorage' property from 'Window'".
//
// Kept byte-identical to the dashboard and landing-page copies so the three can
// be lifted into a shared package in one move.

const DEVICE_ID_KEY = 'aksademia-device-id';

// Holds the id for the page lifetime when storage is unavailable. Minting a
// fresh uuid per call would present as a brand-new device on every request and
// flood device registration.
let memoizedId: string | null = null;

function randomId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // `crypto.randomUUID` needs a secure context and is missing in some
    // webviews. This is a device-correlation id, not a secret, so a non-crypto
    // fallback is acceptable and beats throwing on the request path.
    return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function inMemoryId(): string {
  if (!memoizedId) memoizedId = randomId();
  return memoizedId;
}

export function getDeviceId(): string {
  if (typeof window === 'undefined') return '';

  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY);
    if (stored) return stored;
  } catch {
    // Reads blocked outright — writes will fail too, so skip them.
    return inMemoryId();
  }

  // Nothing stored yet. Mint through inMemoryId() so a failed write still
  // leaves this call and every later one agreeing on the same value.
  const id = inMemoryId();
  try {
    localStorage.setItem(DEVICE_ID_KEY, id);
  } catch {
    // Storage blocked or full — the in-memory id still serves the header.
  }
  return id;
}
